import {
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import {
  DEFAULT_DONATION_CONFIG,
  type DonationConfig,
  type DonationTrigger,
  type TriggerCondition,
  type UserEngagementMetrics,
  type DonationPromptRecord,
  type MetricType,
  type DonationClickAction,
} from '@/types/donation';
import type { UserStats } from '@/types';

// ============================================
// PATH HELPERS
// ============================================

function getDonationConfigPath(): string {
  return `artifacts/${appId}/config/donation`;
}

function getPromptHistoryPath(userId: string): string {
  return `artifacts/${appId}/users/${userId}/donation_prompts`;
}

// ============================================
// CONFIGURATION FUNCTIONS
// ============================================

/**
 * Merge config data with defaults to ensure all properties exist.
 */
function mergeWithDefaults(data: Record<string, unknown>): DonationConfig {
  return {
    enabled: (data.enabled as boolean) ?? DEFAULT_DONATION_CONFIG.enabled,
    globalCooldownHours:
      (data.globalCooldownHours as number) ?? DEFAULT_DONATION_CONFIG.globalCooldownHours,
    dismissCooldownHours:
      (data.dismissCooldownHours as number) ?? DEFAULT_DONATION_CONFIG.dismissCooldownHours,
    maxPromptsPerWeek:
      (data.maxPromptsPerWeek as number) ?? DEFAULT_DONATION_CONFIG.maxPromptsPerWeek,
    triggers: (data.triggers as DonationTrigger[]) ?? DEFAULT_DONATION_CONFIG.triggers,
    display: {
      position:
        ((data.display as Record<string, unknown>)
          ?.position as DonationConfig['display']['position']) ??
        DEFAULT_DONATION_CONFIG.display.position,
      animationStyle:
        ((data.display as Record<string, unknown>)
          ?.animationStyle as DonationConfig['display']['animationStyle']) ??
        DEFAULT_DONATION_CONFIG.display.animationStyle,
      autoDismissSeconds:
        ((data.display as Record<string, unknown>)?.autoDismissSeconds as number | null) ??
        DEFAULT_DONATION_CONFIG.display.autoDismissSeconds,
    },
    updatedAt: data.updatedAt as DonationConfig['updatedAt'],
    updatedBy: data.updatedBy as string | undefined,
  };
}

/**
 * Get the donation configuration.
 * Returns default config if none exists.
 */
export async function getDonationConfig(): Promise<DonationConfig> {
  const configRef = doc(db, getDonationConfigPath());
  const configSnap = await getDoc(configRef);

  if (configSnap.exists()) {
    return mergeWithDefaults(configSnap.data());
  }

  return DEFAULT_DONATION_CONFIG as DonationConfig;
}

/**
 * Subscribe to donation configuration changes.
 */
export function subscribeToDonationConfig(callback: (config: DonationConfig) => void): () => void {
  const configRef = doc(db, getDonationConfigPath());

  return onSnapshot(
    configRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(mergeWithDefaults(snapshot.data()));
      } else {
        callback(DEFAULT_DONATION_CONFIG as DonationConfig);
      }
    },
    (error) => {
      console.error('Error subscribing to donation config:', error);
      callback(DEFAULT_DONATION_CONFIG as DonationConfig);
    }
  );
}

// ============================================
// PROMPT HISTORY FUNCTIONS
// ============================================

/**
 * Get recent prompt history for a user.
 */
export async function getPromptHistory(
  userId: string,
  limitCount: number = 20
): Promise<DonationPromptRecord[]> {
  const historyRef = collection(db, getPromptHistoryPath(userId));
  const q = query(historyRef, orderBy('shownAt', 'desc'), limit(limitCount));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as DonationPromptRecord[];
}

/**
 * Subscribe to prompt history changes.
 */
export function subscribeToPromptHistory(
  userId: string,
  callback: (history: DonationPromptRecord[]) => void,
  limitCount: number = 20
): () => void {
  const historyRef = collection(db, getPromptHistoryPath(userId));
  const q = query(historyRef, orderBy('shownAt', 'desc'), limit(limitCount));

  return onSnapshot(
    q,
    (snapshot) => {
      const history = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as DonationPromptRecord[];
      callback(history);
    },
    (error) => {
      console.error('Error subscribing to prompt history:', error);
      callback([]);
    }
  );
}

/**
 * Record a prompt being shown.
 */
export async function recordPromptShown(userId: string, trigger: DonationTrigger): Promise<string> {
  const historyRef = collection(db, getPromptHistoryPath(userId));

  const record: Omit<DonationPromptRecord, 'id'> = {
    triggerId: trigger.id,
    triggerName: trigger.name,
    shownAt: Timestamp.now(),
    action: null,
  };

  const docRef = await addDoc(historyRef, record);
  return docRef.id;
}

/**
 * Record user action on a prompt.
 */
export async function recordPromptAction(
  userId: string,
  promptId: string,
  action: 'clicked' | 'dismissed' | 'expired'
): Promise<void> {
  const promptRef = doc(db, getPromptHistoryPath(userId), promptId);

  await updateDoc(promptRef, {
    action,
    actionAt: serverTimestamp(),
  });
}

// ============================================
// MANUAL TRIGGER SENDING
// ============================================

/**
 * Manually send a donation trigger to specific users.
 * This bypasses cooldown checks and immediately shows the prompt.
 */
export async function sendManualTrigger(
  userIds: string[],
  trigger: DonationTrigger,
  options?: {
    customMessage?: string;
    clickActionOverride?: DonationClickAction;
  }
): Promise<{ success: string[]; failed: string[] }> {
  const results = { success: [] as string[], failed: [] as string[] };

  // Create a modified trigger with custom message if provided
  const messageToSend = options?.customMessage || trigger.message;

  // Use override action if provided, otherwise use trigger's default action
  const clickAction = options?.clickActionOverride || trigger.clickAction;

  // Send to each user
  for (const userId of userIds) {
    try {
      const historyRef = collection(db, getPromptHistoryPath(userId));

      const record: Omit<DonationPromptRecord, 'id'> = {
        triggerId: `manual_${trigger.id}`,
        triggerName: `[Manual] ${trigger.name}`,
        shownAt: Timestamp.now(),
        action: null,
        // Store full display data for the client to show
        message: messageToSend,
        icon: trigger.icon,
        isManual: true,
        // Include click action (override or from trigger)
        clickAction,
      };

      await addDoc(historyRef, record);
      results.success.push(userId);
    } catch (error) {
      console.error(`Failed to send trigger to user ${userId}:`, error);
      results.failed.push(userId);
    }
  }

  return results;
}

/**
 * Send a custom one-time donation prompt to specific users.
 * Creates a temporary trigger just for this send.
 */
export async function sendCustomPrompt(
  userIds: string[],
  message: string,
  icon: string = 'heart',
  clickAction?: DonationClickAction
): Promise<{ success: string[]; failed: string[] }> {
  const results = { success: [] as string[], failed: [] as string[] };

  // Send to each user
  for (const userId of userIds) {
    try {
      const historyRef = collection(db, getPromptHistoryPath(userId));

      const record: Omit<DonationPromptRecord, 'id'> = {
        triggerId: `custom_${Date.now()}`,
        triggerName: '[Manual] Custom Prompt',
        shownAt: Timestamp.now(),
        action: null,
        message,
        icon,
        isManual: true,
        clickAction,
      };

      await addDoc(historyRef, record);
      results.success.push(userId);
    } catch (error) {
      console.error(`Failed to send custom prompt to user ${userId}:`, error);
      results.failed.push(userId);
    }
  }

  return results;
}

// ============================================
// COOLDOWN CHECKING
// ============================================

/**
 * Check if user is in global cooldown period.
 */
export function isInGlobalCooldown(
  config: DonationConfig,
  promptHistory: DonationPromptRecord[]
): boolean {
  if (promptHistory.length === 0) return false;

  const lastPrompt = promptHistory[0]; // Already sorted by shownAt desc
  if (!lastPrompt.shownAt) return false;

  const lastShownMs = lastPrompt.shownAt.toMillis();
  const cooldownMs = config.globalCooldownHours * 60 * 60 * 1000;
  const now = Date.now();

  return now - lastShownMs < cooldownMs;
}

/**
 * Check if user is in dismiss cooldown (longer cooldown after dismissing).
 */
export function isInDismissCooldown(
  config: DonationConfig,
  promptHistory: DonationPromptRecord[]
): boolean {
  // Find the most recent dismissed prompt
  const lastDismissed = promptHistory.find((p) => p.action === 'dismissed');
  if (!lastDismissed || !lastDismissed.actionAt) return false;

  const dismissedMs =
    lastDismissed.actionAt instanceof Timestamp ? lastDismissed.actionAt.toMillis() : Date.now();
  const cooldownMs = config.dismissCooldownHours * 60 * 60 * 1000;
  const now = Date.now();

  return now - dismissedMs < cooldownMs;
}

/**
 * Check if user has exceeded weekly prompt limit.
 */
export function hasExceededWeeklyLimit(
  config: DonationConfig,
  promptHistory: DonationPromptRecord[]
): boolean {
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  const promptsThisWeek = promptHistory.filter((p) => {
    if (!p.shownAt) return false;
    return p.shownAt.toMillis() > oneWeekAgo;
  });

  return promptsThisWeek.length >= config.maxPromptsPerWeek;
}

/**
 * Combined cooldown check.
 */
export function isInCooldown(
  config: DonationConfig,
  promptHistory: DonationPromptRecord[]
): boolean {
  return (
    isInGlobalCooldown(config, promptHistory) ||
    isInDismissCooldown(config, promptHistory) ||
    hasExceededWeeklyLimit(config, promptHistory)
  );
}

// ============================================
// TRIGGER EVALUATION
// ============================================

/**
 * Get the value of a metric from the combined metrics/stats.
 */
function getMetricValue(
  metric: MetricType,
  engagementMetrics: UserEngagementMetrics | null,
  userStats: UserStats | null
): number {
  if (!engagementMetrics && !userStats) return 0;

  switch (metric) {
    // From engagement metrics
    case 'messagesThisDay':
      return engagementMetrics?.messagesThisDay || 0;
    case 'messagesThisWeek':
      return engagementMetrics?.messagesThisWeek || 0;
    case 'reactionsGivenThisDay':
      return engagementMetrics?.reactionsGivenThisDay || 0;
    case 'reactionsReceivedThisDay':
      return engagementMetrics?.reactionsReceivedThisDay || 0;
    case 'checkInsThisWeek':
      return engagementMetrics?.checkInsThisWeek || 0;
    case 'sessionMinutes':
      return engagementMetrics?.sessionMinutesToday || 0;
    case 'consecutiveActiveDays':
      return engagementMetrics?.consecutiveActiveDays || 0;

    // Reading engagement metrics
    case 'readingTimeThisDay':
      return engagementMetrics?.readingTimeThisDay || 0;
    case 'readingTimeThisWeek':
      return engagementMetrics?.readingTimeThisWeek || 0;
    case 'versesReadThisDay':
      return engagementMetrics?.versesReadThisDay || 0;
    case 'versesReadThisWeek':
      return engagementMetrics?.versesReadThisWeek || 0;
    case 'resourcesViewedThisWeek':
      return engagementMetrics?.resourcesViewedThisWeek || 0;
    case 'parshasCompletedTotal':
      return engagementMetrics?.parshasCompletedTotal || 0;
    case 'currentReadingStreak':
      return engagementMetrics?.currentReadingStreak || 0;

    // From user stats
    case 'currentStreak':
      return userStats?.streak || 0;
    case 'checkInsTotal':
      return userStats?.totalClasses || 0;

    // Derived metrics (would need additional data sources)
    case 'prayersAddedTotal':
      return 0; // Would need to query prayers collection
    case 'badgesUnlocked': {
      // Calculate based on totalClasses thresholds
      const classes = userStats?.totalClasses || 0;
      const thresholds = [1, 5, 10, 18, 26, 40, 54, 70, 100, 154];
      return thresholds.filter((t) => classes >= t).length;
    }
    case 'prayerDonationClicksThisWeek':
      return engagementMetrics?.prayerDonationClicksThisWeek || 0;

    default:
      return 0;
  }
}

/**
 * Evaluate a single condition.
 */
function evaluateCondition(
  condition: TriggerCondition,
  engagementMetrics: UserEngagementMetrics | null,
  userStats: UserStats | null
): boolean {
  const value = getMetricValue(condition.metric, engagementMetrics, userStats);

  switch (condition.operator) {
    case 'gte':
      return value >= condition.value;
    case 'lte':
      return value <= condition.value;
    case 'eq':
      return value === condition.value;
    case 'between':
      return value >= condition.value && value <= (condition.maxValue || condition.value);
    default:
      return false;
  }
}

/**
 * Evaluate a trigger's conditions.
 */
function evaluateTriggerConditions(
  trigger: DonationTrigger,
  engagementMetrics: UserEngagementMetrics | null,
  userStats: UserStats | null
): boolean {
  if (!trigger.enabled || trigger.conditions.length === 0) {
    return false;
  }

  const results = trigger.conditions.map((condition) =>
    evaluateCondition(condition, engagementMetrics, userStats)
  );

  if (trigger.conditionLogic === 'AND') {
    return results.every((r) => r);
  } else {
    return results.some((r) => r);
  }
}

/**
 * Evaluate all triggers and return the highest priority matching trigger.
 * Returns null if no triggers match or user is in cooldown.
 */
export function evaluateTriggers(
  engagementMetrics: UserEngagementMetrics | null,
  userStats: UserStats | null,
  config: DonationConfig,
  promptHistory: DonationPromptRecord[]
): DonationTrigger | null {
  // Check if donation system is enabled
  if (!config.enabled) {
    return null;
  }

  // Check cooldowns
  if (isInCooldown(config, promptHistory)) {
    return null;
  }

  // Evaluate all enabled triggers
  const matchingTriggers = config.triggers
    .filter((trigger) => evaluateTriggerConditions(trigger, engagementMetrics, userStats))
    .sort((a, b) => b.priority - a.priority); // Higher priority first

  return matchingTriggers.length > 0 ? matchingTriggers[0] : null;
}

// ============================================
// MESSAGE VARIABLE SUBSTITUTION
// ============================================

/**
 * Substitute variables in trigger message.
 * Supports: {checkInsTotal}, {currentStreak}, {messagesThisDay}, etc.
 */
export function substituteMessageVariables(
  message: string,
  engagementMetrics: UserEngagementMetrics | null,
  userStats: UserStats | null
): string {
  const metrics: MetricType[] = [
    'checkInsThisWeek',
    'checkInsTotal',
    'currentStreak',
    'messagesThisDay',
    'messagesThisWeek',
    'reactionsGivenThisDay',
    'reactionsReceivedThisDay',
    'sessionMinutes',
    'consecutiveActiveDays',
    'prayersAddedTotal',
    'badgesUnlocked',
    'prayerDonationClicksThisWeek',
    // Reading engagement metrics
    'readingTimeThisDay',
    'readingTimeThisWeek',
    'versesReadThisDay',
    'versesReadThisWeek',
    'resourcesViewedThisWeek',
    'parshasCompletedTotal',
    'currentReadingStreak',
  ];

  let result = message;
  for (const metric of metrics) {
    const value = getMetricValue(metric, engagementMetrics, userStats);
    result = result.replace(new RegExp(`\\{${metric}\\}`, 'g'), value.toString());
  }

  return result;
}
